import { Button } from 'common/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'common/ui/dialog'
import { AlertTriangle, Info } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from 'utils/cn'

export interface NotificationUserInfoItem {
  id: string
  name?: string
  email?: string
  phone?: string
}

export interface NotificationUserInfoPaging {
  count: number
  page: number
  page_size: number
}

interface NotificationUserInfoProps {
  title?: string
  /** The current page of users when `paging` is set, else all of them. */
  items: NotificationUserInfoItem[]
  className?: string
  /**
   * Whether to show as a warning/alert style
   */
  isAlert?: boolean
  /** Server paging of `items`; the totals and the dialog pager use it. */
  paging?: NotificationUserInfoPaging | null
  onPageChange?: (page: number) => void
}

const DISPLAY_ITEMS = 3

export default function NotificationUserInfo({
  title = 'Thông báo',
  items,
  className,
  isAlert = true,
  paging,
  onPageChange,
}: NotificationUserInfoProps) {
  const total = paging?.count ?? items?.length ?? 0
  const hasMore = total > DISPLAY_ITEMS
  const displayItems = (items || []).slice(0, DISPLAY_ITEMS)
  const page = paging?.page ?? 1
  const pageCount = paging
    ? Math.max(1, Math.ceil(paging.count / Math.max(1, paging.page_size)))
    : 1

  // Checked on the total, not items: items is empty while another page loads,
  // and unmounting then would close the dialog.
  if (total === 0) {
    return null
  }

  const IconComponent = isAlert ? AlertTriangle : Info
  const iconColor = 'text-amber-600'
  const textColor = 'text-amber-800'
  const bgColor = 'bg-gradient-to-r from-amber-50 to-yellow-50'
  const borderColor = 'border-amber-300'
  const shadowColor = 'shadow-amber-100'

  return (
    <div className={cn('w-full border-2 rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300', borderColor, bgColor, shadowColor, className)}>
      {/* Header */}
      <div className="flex items-center gap-4 p-5 border-b border-amber-200/60">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center shadow-sm">
          <IconComponent className={cn('h-5 w-5', iconColor)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn('font-bold text-lg leading-tight', textColor)}>
            {title}
          </div>
          <div className="text-sm text-amber-600 font-medium mt-0.5">
            {total} người dùng cần xử lý
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex flex-wrap gap-3">
          {displayItems.map((item) => (
            <Link
              key={item.id}
              to={`/investment/users/${item.id}`}
              className="group flex-1 min-w-[200px] max-w-[280px] p-4 bg-white/80 border border-amber-200/70 rounded-xl hover:bg-white hover:border-amber-300 hover:shadow-md transition-all duration-200 block"
            >
              <div className="space-y-1">
                <div className="text-amber-800 group-hover:text-amber-900 font-semibold text-sm leading-tight">
                  {item.name || 'Người dùng'}
                </div>
                {item.email && (
                  <div className="text-xs text-amber-600 truncate">
                    {item.email}
                  </div>
                )}
            
              </div>
            </Link>
          ))}

          {hasMore && (
            <Dialog>
              <DialogTrigger asChild>
                <div className="group flex-1 min-w-[200px] max-w-[280px] p-4 bg-gradient-to-br from-amber-100/80 to-amber-200/80 border border-amber-300/70 rounded-xl hover:from-amber-200 hover:to-amber-300 hover:border-amber-400 hover:shadow-md transition-all duration-200 cursor-pointer">
                  <div className="space-y-1 text-center">
                    <div className="text-amber-800 group-hover:text-amber-900 font-semibold text-sm">
                      Xem tất cả
                    </div>
                    <div className="text-xs text-amber-600">
                      {total - DISPLAY_ITEMS} người dùng khác
                    </div>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-7xl max-h-[95vh] overflow-hidden p-0">
                <DialogHeader className="p-6 border-b border-amber-200/50 bg-gradient-to-r from-amber-50/50 to-yellow-50/50">
                  <DialogTitle className="flex items-center gap-4 text-2xl">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center shadow-md">
                      <IconComponent className={cn('h-6 w-6', iconColor)} />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-amber-900">{title}</div>
                      <div className="text-sm text-amber-600 font-medium mt-1">
                        Danh sách tất cả {total} người dùng cần xử lý
                      </div>
                    </div>
           
                  </DialogTitle>
                </DialogHeader>
                
                <div className="px-6 py-4">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-sm text-amber-700 font-medium">
                      Nhấp vào từng người dùng để xem chi tiết
                    </div>
                    {onPageChange && pageCount > 1 && (
                      <div className="flex items-center gap-2 text-sm text-amber-700">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page <= 1}
                          onClick={() => onPageChange(page - 1)}
                        >
                          Trước
                        </Button>
                        <span>
                          Trang {page} / {pageCount}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page >= pageCount}
                          onClick={() => onPageChange(page + 1)}
                        >
                          Sau
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <div className="overflow-y-auto max-h-[60vh] pr-2">
                    <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4">
                      {items.map((item, index) => (
                        <Link
                          key={item.id}
                          to={`/investment/users/${item.id}`}
                          className="group relative p-4 bg-white border-2 border-amber-200/60 rounded-xl hover:bg-amber-50 hover:border-amber-300 hover:shadow-lg transition-all duration-200 block no-underline"
                        >
                          <div className="space-y-2 pr-8">
                            <div className="text-amber-800 group-hover:text-amber-900 font-semibold text-sm leading-tight line-clamp-2">
                              {item.name || item.email || 'Người dùng'}
                            </div>
                            {item.email && (
                              <div className="text-xs text-amber-600 truncate" title={item.email}>
                                {item.email}
                              </div>
                            )}
                            {item.phone && (
                              <div className="text-xs text-amber-500 truncate" title={item.phone}>
                                {item.phone}
                              </div>
                            )}
                          </div>
                          <div className="absolute top-2 right-2 text-amber-400 group-hover:text-amber-500 font-bold text-lg">
                            →
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </div>
  )
}


